import type { ReactElement } from 'react'
import type { MessagePreviewView } from './notification-template-types'

/**
 * Props for {@link MessagePreviewPane}.
 *
 * @property title - The pane's heading, also its accessible region name.
 * @property preview - The rendered message, or null before the first render.
 * @property error - A refused render's message (announced), or null.
 * @property emptyText - What the pane says before anything is rendered.
 * @property subjectLabel - The label in front of a rendered subject.
 */
export interface MessagePreviewPaneProps {
	title: string
	preview: MessagePreviewView | null
	error: string | null
	emptyText: string
	subjectLabel: string
}

/** The rendered body (and, for an email, its subject), the refused render, or the empty text. */
function PreviewContent(props: MessagePreviewPaneProps): ReactElement {
	if (props.error) {
		return (
			<p role="alert" className="text-ds-sm text-ds-danger">
				{props.error}
			</p>
		)
	}
	if (!props.preview) return <p className="text-ds-sm text-ds-text-muted">{props.emptyText}</p>
	return (
		<>
			{props.preview.subject !== null ? (
				<p className="text-ds-sm text-ds-text">
					<span className="font-semibold">{props.subjectLabel}: </span>
					{props.preview.subject}
				</p>
			) : null}
			<p className="whitespace-pre-wrap text-ds-sm text-ds-text">{props.preview.body}</p>
		</>
	)
}

/** A rendered notification message as plain text (TO-140). Pure. */
export function MessagePreviewPane(props: MessagePreviewPaneProps): ReactElement {
	return (
		<section
			aria-label={props.title}
			aria-live="polite"
			className="flex flex-col gap-2 rounded-ds-md border border-ds-border bg-ds-surface p-4"
		>
			<h3 className="text-ds-sm font-semibold text-ds-text">{props.title}</h3>
			<PreviewContent {...props} />
		</section>
	)
}
